import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { FormProvider, useForm } from "react-hook-form";

import {
    getOrganizationSettings,
    IOrganization,
    IOrganizationSettingsUpdate,
    readScopesEnabled,
    updateOrganizationSettings,
} from "@/api";
import { Spinner } from "@/components/ui/spinner";

import { SettingsForm } from "./SettingsForm";

interface IProps {
    organization: IOrganization;
}

export const OrganizationSettings = ({ organization }: IProps) => {
    const { data: organizationSettings, isPending } = useQuery({
        queryKey: ["admin-organization-settings", organization.id],
        queryFn: () => getOrganizationSettings(organization.id),
    });

    if (isPending) {
        return (
            <div className="flex flex-col gap-4 p-4">
                <h2 className="text-xl font-semibold">Settings</h2>
                <Spinner className="size-5" />
            </div>
        );
    }

    if (!organizationSettings) return null;

    return (
        <div className="flex flex-col gap-4 p-4">
            <h2 className="text-xl font-semibold">Settings</h2>
            <SettingsFormShell
                key={organization.id}
                organizationId={organization.id}
                defaultValues={{
                    ...organizationSettings,
                    scopes_enabled: readScopesEnabled(organizationSettings),
                }}
            />
        </div>
    );
};

interface ShellProps {
    organizationId: number;
    defaultValues: IOrganizationSettingsUpdate;
}

function SettingsFormShell({ organizationId, defaultValues }: ShellProps) {
    const queryClient = useQueryClient();
    const methods = useForm<IOrganizationSettingsUpdate>({ defaultValues });
    const { dirtyFields } = methods.formState;
    const mutate = useMutation({
        mutationFn: (data: IOrganizationSettingsUpdate) =>
            updateOrganizationSettings(organizationId, data),
        onSuccess: (response, submitted) => {
            queryClient.invalidateQueries({
                queryKey: ["admin-organization-settings", organizationId],
            });
            if (submitted.scopes_enabled !== undefined) {
                methods.resetField("scopes_enabled", {
                    defaultValue: submitted.scopes_enabled,
                });
            }
            const cancelled = response.cancelled_invitations ?? 0;
            const message =
                cancelled > 0
                    ? `Settings updated. ${cancelled} pending invitation${
                          cancelled === 1 ? "" : "s"
                      } auto-cancelled.`
                    : "Settings updated successfully!";
            enqueueSnackbar(message, {
                variant: "success",
                persist: false,
            });
        },
        onError: () => {
            enqueueSnackbar("Failed to update settings", {
                variant: "error",
                persist: false,
            });
        },
    });
    const onSave = (data: IOrganizationSettingsUpdate) => {
        const { scopes_enabled, ...unchangedScopes } = data;
        mutate.mutate(
            dirtyFields.scopes_enabled
                ? { ...unchangedScopes, scopes_enabled }
                : unchangedScopes
        );
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSave)}>
                <SettingsForm isLoading={mutate.isPending} />
            </form>
        </FormProvider>
    );
}
